import { useCallback, useEffect, useRef, useState } from 'react'
import { streamChatMessage, fetchChatReplies, type BookingDraft, type PlumberResultCard } from '../lib/api/chat'
import { logStatsigEvent } from '../lib/statsig'
import { ASSISTANT_NAME } from '../lib/persona'

export interface ChatTurn {
  role: 'user' | 'assistant'
  content: string
  /** Attached to the assistant turn that drafted it (see tools.ts -
   *  create_emergency_request never submits anything itself, this is purely
   *  what BookingConfirmCard renders). UI-only - never sent back to the
   *  server as conversation history; the model's own preceding text already
   *  carries that meaning. */
  confirmBooking?: BookingDraft
  /** Set on the assistant turn that called show_category_picker - renders
   *  CategoryPickerCard below it. Same UI-only rule as confirmBooking above. */
  showCategoryPicker?: boolean
  /** Set on the assistant turn that called find_plumbers_nearby and got at
   *  least one result - renders PlumberResultsCard below it. Same UI-only
   *  rule as confirmBooking above. */
  plumberResults?: PlumberResultCard[]
  /** True for a fact injected straight into history via recordSystemNote
   *  (e.g. "the request was just submitted, job ref X") rather than streamed
   *  from the model. Unlike confirmBooking/showCategoryPicker, its `content`
   *  DOES reach the server as normal conversation history (role+content is
   *  all sendMessage ever sends) so the model actually knows the fact on the
   *  next turn - it just never renders as a chat bubble, since the customer
   *  already saw the real result (the confirm card flipping to "Submitted"). */
  hidden?: boolean
  /** Set on a turn injected from fetchChatReplies (issue #51) - a real team
   *  member's Discord reply to this conversation, not the model. Rendered
   *  distinctly (ChatPanel.tsx) so the customer can tell a person, not the
   *  bot, wrote it. Its `content` is real text, so it reaches the server as
   *  normal history on the next turn the same way any other assistant turn
   *  does - the model should see and build on what the human said. */
  fromHuman?: boolean
}

// sessionStorage, deliberately not localStorage (see useFormSession.ts for
// that convention) - a chat conversation is disposable, low-stakes, and
// "session-only" should mean what sessionStorage actually does: gone when
// the tab closes, not carried around indefinitely like an in-progress
// emergency request.
const SESSION_KEY = 'fd_chat_session'

// One id per conversation, sent to the server so it can log every turn into
// the same Discord thread (issue #50, see logChatTurn/discord.ts). Stored
// separately from the messages themselves so it survives independently of
// whatever isEmptyPlaceholder filtering happens to messages.
const CONVERSATION_ID_KEY = 'fd_chat_conversation_id'

function getOrCreateConversationId(): string {
  try {
    const existing = sessionStorage.getItem(CONVERSATION_ID_KEY)
    if (existing) return existing
    const fresh = crypto.randomUUID()
    sessionStorage.setItem(CONVERSATION_ID_KEY, fresh)
    return fresh
  } catch {
    // Private browsing / storage disabled - chat still works, this
    // conversation just won't be correlated across a reload (same
    // degradation as loadStoredMessages below for the messages themselves).
    return crypto.randomUUID()
  }
}

/** Static opening line, shown before the customer says anything. It's a real
 *  first turn in `messages` (not a separate empty-state render) so it stays
 *  on screen once they start chatting instead of vanishing the moment
 *  `messages` goes from empty to non-empty.
 *
 *  This is the one place the introduction happens (issue #78). Because it is a
 *  fixed string and not model output, the prompt's hard ban on asking "what's
 *  wrong" doesn't apply to it - that rule exists to force the model through
 *  show_category_picker, and there is no tool call to make before the customer
 *  has said anything at all. The prompt is told the greeting has already run,
 *  so the model must not introduce itself again. */
export const CHAT_GREETING = `Hi, I'm ${ASSISTANT_NAME}. What's the plumbing emergency?`
const INITIAL_MESSAGES: ChatTurn[] = [{ role: 'assistant', content: CHAT_GREETING }]

/** True for a leftover placeholder bubble that never got real content - never
 *  something intentional (recordSystemNote always writes real text, and a
 *  patched-in reply always ends up non-empty), always an artifact of a turn
 *  that streamed nothing back (see the isEmptyPlaceholder filter in
 *  sendMessage's finally block for the full explanation). Shared with
 *  loadStoredMessages so a session already poisoned by one - saved to
 *  sessionStorage before this filtering existed - self-heals on next load
 *  instead of permanently 400ing every future message forever.
 *
 *  Must check every UI-only attachment (confirmBooking, showCategoryPicker,
 *  plumberResults), not just content - a turn where the model called a tool
 *  and said nothing else in the same breath has empty `content` but is very
 *  much not an empty turn to discard, it's the whole point of that reply. */
function isEmptyPlaceholder(m: ChatTurn): boolean {
  return m.role === 'assistant' && m.content === '' && !m.confirmBooking && !m.showCategoryPicker && !m.plumberResults?.length
}

function loadStoredMessages(): ChatTurn[] {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    if (!raw) return INITIAL_MESSAGES
    const parsed = (JSON.parse(raw) as ChatTurn[]).filter(m => !isEmptyPlaceholder(m))
    return parsed.length ? parsed : INITIAL_MESSAGES
  } catch {
    return INITIAL_MESSAGES
  }
}

function storeMessages(messages: ChatTurn[]) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(messages))
  } catch { /* ignore - e.g. private browsing quota */ }
}

/** Merges `patch` into the current last turn, only if it's the assistant's -
 *  every streamed event (text delta, confirm_booking, category_picker) lands
 *  on that same in-progress turn, so this is the one place that logic lives. */
function patchLastAssistantTurn(current: ChatTurn[], patch: Partial<ChatTurn>): ChatTurn[] {
  const next = [...current]
  const last = next[next.length - 1]
  if (last?.role === 'assistant') next[next.length - 1] = { ...last, ...patch }
  return next
}

export function useChatSession() {
  const [messages, setMessages] = useState<ChatTurn[]>(loadStoredMessages)
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const conversationIdRef = useRef(getOrCreateConversationId())
  // Set once escalate_stuck fires (issue #51) - drives the reply-poll effect
  // below. Not persisted across a reload: a fresh page load re-reads
  // sessionStorage's messages but has no way to know whether escalation is
  // still pending, so polling only ever resumes for the current mount - an
  // acceptable trade-off for a feature this low-stakes.
  const [escalated, setEscalated] = useState(false)
  const repliesSeenRef = useRef(0)

  useEffect(() => { storeMessages(messages) }, [messages])

  // Polls for a human's Discord reply landing in this conversation, for as
  // long as the chat widget stays open after an escalation. Matches
  // JobStatus.tsx's existing live-update-via-polling convention rather than
  // introducing a websocket - see the issue #51 plan for why.
  useEffect(() => {
    if (!escalated) return
    let cancelled = false
    const poll = async (): Promise<void> => {
      try {
        const { replies, resolved } = await fetchChatReplies(conversationIdRef.current, repliesSeenRef.current)
        if (cancelled) return
        if (replies.length > 0) {
          repliesSeenRef.current += replies.length
          setMessages(current => [
            ...current,
            ...replies.map((r): ChatTurn => ({ role: 'assistant', content: r.text, fromHuman: true })),
          ])
        }
        if (resolved) setEscalated(false)
      } catch {
        // Best-effort - a failed poll just tries again next tick, never
        // surfaces an error to the customer over a background check.
      }
    }
    void poll()
    const id = setInterval(() => void poll(), 8000)
    return () => { cancelled = true; clearInterval(id) }
  }, [escalated])

  // ChatPanel fully unmounts when the customer closes the chat (ChatWidget.tsx
  // conditionally renders it) - without this, closing mid-reply doesn't cancel
  // the request: it keeps running server-side, still burns real LLM cost for a
  // response nobody will see, and worse, any tool call in it (support ticket,
  // job draft) still fires even though the chat window is gone.
  useEffect(() => () => abortRef.current?.abort(), [])

  const sendMessage = useCallback(async (text: string) => {
    const trimmed = text.trim()
    if (!trimmed || isStreaming) return

    setError(null)
    // Chat engagement funnel (see statsig.ts) - messageNumber is derived from
    // the existing history rather than a separate counter, so it stays
    // correct across a reload within the same sessionStorage-backed session
    // rather than resetting to 1 every time the component remounts.
    const messageNumber = messages.filter(m => m.role === 'user').length + 1
    logStatsigEvent('chat_message_sent', messageNumber, { messageNumber: String(messageNumber) })
    // Only role+content goes to the server - confirmBooking is UI-only. Also
    // never send a blank turn: a previous turn can legitimately leave one
    // behind (see isEmptyPlaceholder) between this state read and the filter
    // below running, and an old/already-open tab may still have one saved
    // from before this fix existed - either way, sending it is what causes
    // the server's schema to reject the whole array and brick every future
    // message in the session, so strip it here regardless of source.
    const history = [...messages, { role: 'user', content: trimmed } satisfies ChatTurn]
      .filter(m => !isEmptyPlaceholder(m))
      .map(({ role, content }): ChatTurn => ({ role, content }))
    // Placeholder assistant turn - filled in as events stream in below.
    setMessages([...messages, { role: 'user', content: trimmed }, { role: 'assistant', content: '' }])
    setIsStreaming(true)

    const controller = new AbortController()
    abortRef.current = controller

    try {
      await streamChatMessage(history, event => {
        if (event.type === 'text') {
          setMessages(current => {
            const last = current[current.length - 1]
            return patchLastAssistantTurn(current, { content: (last?.content ?? '') + event.text })
          })
        } else if (event.type === 'confirm_booking') {
          setMessages(current => patchLastAssistantTurn(current, { confirmBooking: event.data }))
        } else if (event.type === 'category_picker') {
          setMessages(current => patchLastAssistantTurn(current, { showCategoryPicker: true }))
        } else if (event.type === 'plumber_results') {
          setMessages(current => patchLastAssistantTurn(current, { plumberResults: event.data }))
        } else if (event.type === 'human_support_requested') {
          // Analytics-only - no card, the model's own text already tells the
          // customer what happened. Tied to the real tool result (not guessed
          // from the model's prose), so this only ever fires when a ticket
          // genuinely reached Discord.
          logStatsigEvent('chat_human_support_requested', event.data.ticketRef)
        } else if (event.type === 'chat_escalated') {
          // Starts the reply-poll effect above. Tied to the real tool result,
          // same as human_support_requested - only ever fires when
          // escalate_stuck actually reached Discord.
          logStatsigEvent('chat_escalated')
          setEscalated(true)
        } else if (event.type === 'error') {
          setError(event.message)
        }
      }, controller.signal, conversationIdRef.current)
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
      }
    } finally {
      // Unconditional, covering every way this turn could end up with nothing
      // in its bubble: a genuinely empty stream (the server can legitimately
      // end a turn having sent zero text events - e.g. a tool call with
      // nothing more to say), a mid-stream 'error' event (the server has
      // already sent a 200 and started streaming by then, so it can't fail
      // the request itself - it just emits that event and ends the
      // response, which reaches here as a normal resolution, not a thrown
      // error), an actual exception, or the customer closing the chat
      // mid-reply (AbortError, previously left its placeholder behind too).
      // A leftover empty bubble is never valid content - real replies always
      // end up non-empty, and recordSystemNote always writes real text - so
      // this can never remove anything genuine, only ever an artifact.
      // Critically, this must NOT be scoped to "only the last message": once
      // more turns are sent after a poisoned one, it's no longer last, and a
      // check that only looked at the tail would leave it stuck in the
      // middle of history forever, resent (and rejected) on every future turn.
      setMessages(current => current.filter(m => !isEmptyPlaceholder(m)))
      setIsStreaming(false)
      abortRef.current = null
    }
  }, [messages, isStreaming])

  const stopStreaming = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  /** Injects a fact directly into the conversation without streaming a reply
   *  - see the `hidden` field above. Called by BookingConfirmCard the moment
   *  a submission actually succeeds (or fails), so the model isn't stuck
   *  believing "nothing has happened yet" for the rest of the session. */
  const recordSystemNote = useCallback((text: string) => {
    setMessages(current => [...current, { role: 'assistant', content: text, hidden: true }])
  }, [])

  const clearChat = useCallback(() => {
    abortRef.current?.abort()
    setMessages(INITIAL_MESSAGES)
    setError(null)
    // A cleared chat is a fresh conversation, not a continuation - give it a
    // new id so the next message starts a new Discord thread rather than
    // appending to the one already closed out for the old conversation, and
    // stop polling for replies to an escalation that no longer applies here.
    try {
      sessionStorage.removeItem(SESSION_KEY)
      sessionStorage.removeItem(CONVERSATION_ID_KEY)
    } catch { /* ignore */ }
    conversationIdRef.current = getOrCreateConversationId()
    repliesSeenRef.current = 0
    setEscalated(false)
  }, [])

  return { messages, isStreaming, error, sendMessage, stopStreaming, clearChat, recordSystemNote }
}
